import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import '../styles/GlobalNotifications.css';

type NotificationKind = 'ai_job_completed' | 'ai_job_failed' | 'ai_job_started';

interface NotificationRow {
  id: string;
  user_id: string;
  notification_type: NotificationKind | string;
  title: string;
  message: string;
  created_at: string;
  is_read: boolean;
}

const TOAST_NOTIFICATION_TYPES = ['ai_job_completed', 'ai_job_failed'] as const;
const MAX_VISIBLE_TOASTS = 6;

function isToastNotificationType(value: string): value is (typeof TOAST_NOTIFICATION_TYPES)[number] {
  return TOAST_NOTIFICATION_TYPES.includes(
    value as (typeof TOAST_NOTIFICATION_TYPES)[number]
  );
}

const formatRelativeTime = (iso: string) => {
  const timestamp = Date.parse(String(iso || ''));
  if (!Number.isFinite(timestamp)) return '';
  const diffMs = Date.now() - timestamp;
  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes <= 0) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

export default function GlobalNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<NotificationRow[]>([]);

  useEffect(() => {
    if (!supabase || !user?.id) {
      setNotifications([]);
      return;
    }

    let active = true;
    const userId = user.id;

    const fetchUnread = async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('id, user_id, notification_type, title, message, created_at, is_read')
        .eq('user_id', userId)
        .eq('is_read', false)
        .in('notification_type', [...TOAST_NOTIFICATION_TYPES])
        .order('created_at', { ascending: false })
        .limit(MAX_VISIBLE_TOASTS);

      if (!active || error) {
        if (error) {
          console.error('Failed to fetch unread notifications:', error);
        }
        return;
      }

      setNotifications((data || []) as NotificationRow[]);
    };

    fetchUnread();

    const channel = supabase
      .channel(`notifications-toast-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload: any) => {
          const row = payload?.new as NotificationRow | undefined;
          if (!row || !isToastNotificationType(String(row.notification_type || ''))) {
            return;
          }
          setNotifications((prev) => {
            const deduped = prev.filter((item) => item.id !== row.id);
            return [row, ...deduped].slice(0, MAX_VISIBLE_TOASTS);
          });
        }
      )
      .subscribe();

    return () => {
      active = false;
      channel.unsubscribe();
    };
  }, [user?.id]);

  const dismissNotification = async (notificationId: string) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== notificationId));
    if (!supabase || !user?.id) return;

    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId)
      .eq('user_id', user.id);

    if (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  if (!user?.id || notifications.length === 0) {
    return null;
  }

  return (
    <div className="global-notifications-container" aria-live="polite">
      {notifications.map((notification) => {
        const isError = String(notification.notification_type) === 'ai_job_failed';
        return (
          <div
            key={notification.id}
            className={`global-notification-toast ${isError ? 'error' : 'success'}`}
          >
            <div className="global-notification-icon">
              {isError ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}
            </div>
            <div className="global-notification-content">
              <div className="global-notification-title">{notification.title}</div>
              <div className="global-notification-message">{notification.message}</div>
              <div className="global-notification-time">
                {formatRelativeTime(notification.created_at)}
              </div>
            </div>
            <button
              type="button"
              className="global-notification-close"
              onClick={() => void dismissNotification(notification.id)}
              aria-label="Dismiss notification"
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
